import { FC, ReactNode, useId, useMemo } from 'react'
import { useTheme } from 'styled-components'

import {
  DropzoneProvider,
  DropzoneTextContainer,
  PreviewFile,
  TypeStyle,
  Upload,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroiderySiteMessages } from '../../../../../../containers/EmbroideryContainer/useEmbroiderySiteMessages'
import { ValidationMessage } from '../../../primitives/ValidationMessage/ValidationMessage'

import {
  DigitizationBlurb,
  DigitizationHeader,
  DigitizationPrice,
  DropzoneContainer,
  FileName,
  LearnMoreLink,
  LogoPreview,
  UploadErrors,
  UploadField,
  UploadSpecs,
} from './styles'

export type LogoUploadFieldProps = {
  priceLabel?: string
  file: File | undefined
  /** Shows `file` from this URL instead of its contents, for an upload whose picked file is gone. */
  filePreviewUrl?: string
  onSelectFile(file: File): void
  onRemoveFile(): void
  errorMessage?: string
}

export const LogoUploadField: FC<LogoUploadFieldProps> = ({
  priceLabel,
  file,
  filePreviewUrl,
  onSelectFile,
  onRemoveFile,
  errorMessage,
}) => {
  const { color } = useTheme()
  const isSmallViewport = useIsViewport(ViewportSizes['small'])
  const { logoDigitizationWarning, logoLearnMoreLink } = useEmbroiderySiteMessages()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: copy },
      },
    },
  } = useTranslation()

  const { errors, textLine1, textLine2, mobileLine, removeFileAriaLabel } = copy.logoUploadDropzone
  const errorId = useId()

  const preloadedFile = useMemo<PreviewFile | undefined>(() => {
    if (!file) return undefined
    return Object.assign(file as PreviewFile, {
      preview: filePreviewUrl ?? URL.createObjectURL(file),
    })
  }, [file, filePreviewUrl])

  return (
    <UploadField>
      <DigitizationHeader as='div'>
        <span>{copy.title}</span>
        <DigitizationPrice as='span'>{priceLabel}</DigitizationPrice>
      </DigitizationHeader>

      <DigitizationBlurb>
        {logoDigitizationWarning !== undefined && `${logoDigitizationWarning} `}
        {logoLearnMoreLink !== undefined && (
          <LearnMoreLink
            target='_blank'
            href={logoLearnMoreLink}
            aria-label={copy.ariaLabelLearnMoreLink}
          >
            {copy.learnMore}
          </LearnMoreLink>
        )}
      </DigitizationBlurb>

      <DropzoneProvider initialState={{ initialFiles: preloadedFile ? [preloadedFile] : [] }}>
        <DropzoneContainer
          errorStrings={errors}
          aria-describedby={errorMessage === undefined ? undefined : errorId}
          maxFileNum={1}
          onFilesAdded={files => {
            const [firstFile] = files
            if (firstFile) onSelectFile(firstFile)
          }}
          $hasFiles={file !== undefined}
        >
          <DropzoneTextContainer>
            <Upload color={color.stroke.neutral.text.primary.on.background} />
            {isSmallViewport ? (
              <TypeStyle.BodyMedium>{mobileLine}</TypeStyle.BodyMedium>
            ) : (
              <>
                <TypeStyle.BodyMedium>{textLine1}</TypeStyle.BodyMedium>
                <TypeStyle.BodyMedium>{textLine2}</TypeStyle.BodyMedium>
              </>
            )}
          </DropzoneTextContainer>
          <LogoPreview onRemoveFiles={onRemoveFile} removeLabel={removeFileAriaLabel} />
          <UploadErrors />
        </DropzoneContainer>
        <FileName />
      </DropzoneProvider>

      <ValidationMessage id={errorId} message={errorMessage} />
      {file === undefined && <UploadSpecs>{withBoldFormats(copy.uploadSpecs)}</UploadSpecs>}
    </UploadField>
  )
}

function withBoldFormats(specs: string): ReactNode {
  const formats = /PNG.*?EPS/.exec(specs)
  if (formats === null) return specs

  return (
    <>
      {specs.slice(0, formats.index)}
      <strong>{formats[0]}</strong>
      {specs.slice(formats.index + formats[0].length)}
    </>
  )
}
